import React, { useEffect, useReducer, useRef, useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import moment from 'moment';
import { styled } from '@mui/material';
import uuid from 'uuid';

import FileViewer from 'components/organisms/Milestones/SubmissionPreview/FileViewer';
import { SubmissionCommentSection } from 'components/organisms/Task/TaskSection';
import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useUser2 } from 'contexts/user2';
import courseService from 'services/course';
import feedbackService from 'services/feedback';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Container,
    Divider,
    FeedbackTile,
    Icon,
    IconButton,
    Link,
    MenuItem,
    ResourceChip,
    Select,
    Stack,
    Text,
} from 'shell/components';
import { SUBMISSION_TYPES, TYPES } from 'utils/constants';
import { getAbsoluteURL, getS3FileURL, getUserPreferredFullName } from 'utils/helper';

const renderSubmissionItems = (submission) => {
    return (
        <ResourceChip
            resource={
                submission.submission_type === SUBMISSION_TYPES.FILE ? submission.files[0] : { ...submission.url }
            }
            size={'medium'}
            type={submission.submission_type === SUBMISSION_TYPES.FILE ? 'file' : 'link'}
        />
    );
};

const SubmissionPreview = ({
    device = 'desktop',
    isOpen,
    milestoneId,
    onClickBack,
    getUserByAccountId,
    setConfirmationModal,
    closeConfirmationModal,
}) => {
    const { auth } = useAuth();
    const { milestones } = useInstructor();
    const { groups } = useUser2();

    const initialState = {
        submissions: null,
        submissionsByGroupId: null,
        currentGroupId: '',
        mode: 'detail',
    };
    const [{ submissions, submissionsByGroupId, currentGroupId, mode }, dispatch] = useReducer((state, action) => {
        switch (action.type) {
            case 'FETCH_SUBMISSIONS':
                return {
                    ...state,
                    submissions: action.payload,
                    submissionsByGroupId: action.payload.reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.group_id]: [...(acc[cur.group_id] || []), cur].sort(
                                (a, b) => b.created_on - a.created_on
                            ),
                        };
                    }, {}),
                    currentGroupId: groups.length ? groups[0].id : '',
                };
            case 'SET_CURRENT_GROUP_ID':
                return { ...state, currentGroupId: action.payload };
            case 'SET_PREV_GROUP_ID': {
                const index = groups.findIndex(({ id }) => id === state.currentGroupId);
                if (index === 0) {
                    return { ...state };
                } else {
                    return {
                        ...state,
                        currentGroupId: groups[index - 1].id,
                    };
                }
            }
            case 'SET_NEXT_GROUP_ID': {
                const index = groups.findIndex(({ id }) => id === state.currentGroupId);
                if (index === groups.length - 1) {
                    return { ...state };
                } else {
                    return {
                        ...state,
                        currentGroupId: groups[index + 1].id,
                    };
                }
            }
            case 'SET_MODE': {
                return {
                    ...state,
                    mode: action.payload,
                };
            }
            case 'CREATE_COMMENT': {
                const newSubmissions = state.submissions.map((submission) => {
                    if (submission.id !== action.payload.submission_id) return submission;
                    return {
                        ...submission,
                        comments: [...submission.comments, action.payload],
                    };
                });
                return {
                    ...state,
                    submissions: newSubmissions,
                    submissionsByGroupId: newSubmissions.reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.group_id]: [...(acc[cur.group_id] || []), cur].sort(
                                (a, b) => b.created_on - a.created_on
                            ),
                        };
                    }, {}),
                };
            }
            case 'UPDATE_COMMENT': {
                const newSubmissions = state.submissions.map((submission, index) => {
                    if (submission.id !== action.payload.submission_id) return submission;
                    return {
                        ...submission,
                        comments: submission.comments.map((comment) => {
                            return comment.id === action.payload.id ? action.payload : comment;
                        }),
                    };
                });
                return {
                    ...state,
                    submissions: newSubmissions,
                    submissionsByGroupId: newSubmissions.reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.group_id]: [...(acc[cur.group_id] || []), cur].sort(
                                (a, b) => b.created_on - a.created_on
                            ),
                        };
                    }, {}),
                };
            }
            case 'DELETE_COMMENT': {
                const newSubmissions = state.submissions.map((submission) => {
                    if (submission.id !== action.payload.submission_id) return submission;
                    return {
                        ...submission,
                        comments: submission.comments.filter(({ id }) => id !== action.payload.id),
                    };
                });
                return {
                    ...state,
                    submissions: newSubmissions,
                    submissionsByGroupId: newSubmissions.reduce((acc, cur) => {
                        return {
                            ...acc,
                            [cur.group_id]: [...(acc[cur.group_id] || []), cur].sort(
                                (a, b) => b.created_on - a.created_on
                            ),
                        };
                    }, {}),
                };
            }
            case 'RESET':
                return initialState;
            default:
                throw new Error();
        }
    }, initialState);

    const commentInputRef = useRef(null);

    const [currentFile, setCurrentFile] = useState(null);
    const [currentEditingComment, setCurrentEditingComment] = useState(null);

    const milestone = milestones.find(({ id }) => id === milestoneId);
    const currentGroup = groups.length && currentGroupId ? groups.find(({ id }) => id === currentGroupId) : null;
    const groupSubmissions = currentGroup ? submissionsByGroupId[currentGroup.id] : null;
    const submission = groupSubmissions ? groupSubmissions[0] : null;

    const handleAddComment = (html) => {
        const now = new Date().getTime();
        const newComment = {
            id: uuid.v4(),
            active: true,
            created_on: now,
            modified_on: now,
            owner_account_id: auth.owner_account_id,
            course_id: submission.course_id,
            group_id: submission.group_id,
            text: html.trim(),
            type: TYPES.SUBMISSION,
            submission_id: submission.id,
        };
        dispatch({ type: 'CREATE_COMMENT', payload: newComment });
        feedbackService.createFeedback(newComment);
    };

    const handleEditComment = (html) => {
        const updatedComment = {
            ...currentEditingComment,
            modified_on: new Date().getTime(),
            text: html.trim(),
        };
        dispatch({ type: 'UPDATE_COMMENT', payload: updatedComment });
        feedbackService.updateFeedback(updatedComment.id, updatedComment);
        setCurrentEditingComment(null);
    };

    const handleSelect = (value, comment) => {
        if (value === 'edit') {
            setCurrentEditingComment(comment);
            setTimeout(() => {
                commentInputRef.current.props.editor.commands.focus();
            }, 100);
        }
        if (value === 'delete') {
            setConfirmationModal((current) => ({
                ...current,
                visible: true,
                destructive: true,
                title: 'Delete Comment?',
                content: 'Are you sure you want to permanently delete this comment?',
                confirmText: 'Delete',
                onConfirm: () => {
                    dispatch({ type: 'DELETE_COMMENT', payload: comment });
                    closeConfirmationModal();
                    feedbackService.deleteFeedback(comment.id);
                },
                onCancel: closeConfirmationModal,
            }));
        }
    };

    useEffect(() => {
        const fetchSubmissions = async () => {
            const submissions = await courseService.listCourseSubmissions(
                milestone.course_id,
                JSON.stringify({ active: true, type: TYPES.MILESTONE, milestone_id: milestoneId })
            );
            dispatch({
                type: 'FETCH_SUBMISSIONS',
                payload: submissions.filter(({ group_id }) => {
                    return groups.some(({ id }) => id === group_id);
                }),
            });
        };
        if (isOpen) {
            fetchSubmissions();
        } else {
            dispatch({ type: 'RESET' });
        }
    }, [isOpen, milestoneId, groups]);

    useEffect(() => {
        if (submission && submission.submission_type === SUBMISSION_TYPES.FILE) {
            setCurrentFile(submission.files[0]);
        }
    }, [submission]);

    useEffect(() => {
        dispatch({ type: 'SET_MODE', payload: 'detail' });
    }, [currentGroupId]);

    if (!isOpen) return <></>;

    if (!submissions) {
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: (theme) => theme.palette.ensightful.shades[0],
                }}
            >
                <LogoLoading />
            </Box>
        );
    }

    const Submittable = (
        <PreviewBox device={device}>
            {submission && submission.submission_type === SUBMISSION_TYPES.FILE && currentFile ? (
                <FileViewer device={device} docUrl={getS3FileURL(currentFile.data)} fileType={currentFile.data.type} />
            ) : null}
            {submission && submission.submission_type === SUBMISSION_TYPES.PLAINTEXT ? (
                <Text rich variant={'p2'}>
                    {submission.plaintext}
                </Text>
            ) : null}
            {!submission || submission.submission_type === SUBMISSION_TYPES.URL ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <Text
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                        }}
                        variant={'p2'}
                    >
                        No Viewable File
                    </Text>
                </Box>
            ) : null}
        </PreviewBox>
    );

    const SubmissionData =
        mode === 'detail' ? (
            <Stack spacing={2}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text align={'left'} variant={'sh2'} weight={'semibold'}>
                        Submission
                    </Text>
                    {submission && (
                        <Button
                            color={'neutral'}
                            variant={'tertiary'}
                            onClick={() => dispatch({ type: 'SET_MODE', payload: 'history' })}
                        >
                            View History
                        </Button>
                    )}
                </Stack>
                <Text color={'light'} variant={'p3'}>
                    {submission
                        ? `Submitted on ${moment(submission.created_on).format(
                              'MMM DD [@] hh:mma'
                          )} by ${getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}`
                        : 'Missing submission'}
                </Text>
                {submission && submission.submission_type === SUBMISSION_TYPES.FILE ? (
                    <Stack spacing={1}>
                        {submission.files.map((file) => (
                            <ResourceChip key={file.id} resource={file} size={'medium'} type={'file'} />
                        ))}
                    </Stack>
                ) : null}
                {submission && submission.submission_type === SUBMISSION_TYPES.URL ? (
                    <Link
                        external
                        href={getAbsoluteURL(submission.url.url)}
                        rel={'noopener noreferrer'}
                        startIcon={<Icon kind={'link'} />}
                        target={'_blank'}
                    >
                        {submission.url.url}
                    </Link>
                ) : null}
                <Divider spacing={2} />
                {submission && (
                    <SubmissionCommentSection
                        ref={commentInputRef}
                        submissions={[submission]}
                        getUserByAccountId={getUserByAccountId}
                        handleSelect={handleSelect}
                        currentEditingComment={currentEditingComment}
                        setCurrentEditingComment={setCurrentEditingComment}
                        handleAddComment={handleAddComment}
                        handleEditComment={handleEditComment}
                    />
                )}
            </Stack>
        ) : (
            <>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text align={'left'} variant={'sh2'} weight={'semibold'}>
                        Submission History
                    </Text>
                    <Button
                        color={'neutral'}
                        variant={'tertiary'}
                        onClick={() => dispatch({ type: 'SET_MODE', payload: 'detail' })}
                    >
                        Back
                    </Button>
                </Stack>
                <SubmissionListStack>
                    {groupSubmissions?.map((submission, index) => {
                        return (
                            <SubmissionStack key={submission.id} device={device} spacing={1}>
                                {[SUBMISSION_TYPES.FILE, SUBMISSION_TYPES.URL].includes(submission.submission_type) &&
                                    renderSubmissionItems(submission)}
                                <Text color={'light'} variant={'p3'}>
                                    Submitted on {moment(submission.created_on).format('MMM D [@] h:mma')} by{' '}
                                    {getUserPreferredFullName(getUserByAccountId(submission.owner_account_id))}
                                </Text>
                                <CustomAccordion defaultExpanded={Boolean(index === 0 && submission.comments.length)}>
                                    <AccordionSummary>
                                        <Text variant={'p2'}>Comments ({submission.comments.length})</Text>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={2}>
                                            {submission.comments.map((comment) => (
                                                <FeedbackTile
                                                    key={comment.id}
                                                    author={getUserByAccountId(comment.owner_account_id)}
                                                    feedback={comment}
                                                    getNameById={(id) =>
                                                        getUserPreferredFullName(getUserByAccountId(id))
                                                    }
                                                    options={
                                                        comment.owner_account_id === auth.owner_account_id
                                                            ? [{ key: 'delete', value: 'Delete' }]
                                                            : []
                                                    }
                                                    onSelect={({ key }) => handleSelect(key, comment)}
                                                />
                                            ))}
                                        </Stack>
                                    </AccordionDetails>
                                </CustomAccordion>
                            </SubmissionStack>
                        );
                    })}
                </SubmissionListStack>
            </>
        );

    if (device === 'tablet' || device === 'desktop') {
        return (
            <>
                <HeaderBox device={device}>
                    <HeaderInnerBox>
                        <Stack direction={'row'} justifyContent={'space-between'} spacing={2}>
                            <Stack direction={'row'} spacing={2}>
                                <IconButton
                                    kind={'arrowLeft'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={onClickBack}
                                />
                                <Stack justifyContent={'space-between'}>
                                    <Text noWrap variant={'sh1'} weight={'semibold'}>
                                        {milestone?.title}
                                    </Text>
                                    <Text color={'light'} variant={'p2'}>
                                        Due: {moment(milestone?.end_on).format('MMM DD [at] h:mma')}
                                    </Text>
                                </Stack>
                            </Stack>
                            <Text sx={{ alignSelf: 'flex-end' }} color={'light'} variant={'p2'}>
                                Total Submissions: {Object.keys(submissionsByGroupId || {}).length}/{groups.length}
                            </Text>
                        </Stack>
                        <Stack spacing={2}>
                            <Text color={'light'} variant={'p2'}>
                                Viewing: {groups.findIndex(({ id }) => id === currentGroupId) + 1}/{groups.length}{' '}
                                Submissions
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <Select
                                    size={'small'}
                                    value={currentGroup.id}
                                    onChange={(e) =>
                                        dispatch({ type: 'SET_CURRENT_GROUP_ID', payload: e.target.value })
                                    }
                                >
                                    {groups.map((group) => {
                                        const submissionStatus = !Object.keys(submissionsByGroupId || {}).includes(
                                            group.id
                                        )
                                            ? 'Missing'
                                            : 'Submitted';
                                        return (
                                            <MenuItem key={group.id} value={group.id}>
                                                <Stack
                                                    sx={{ width: '100%' }}
                                                    alignItems={'center'}
                                                    direction={'row'}
                                                    justifyContent={'space-between'}
                                                >
                                                    {group.name}
                                                    <Text
                                                        color={submissionStatus === 'Missing' ? 'light' : 'success'}
                                                        variant={'p2'}
                                                    >
                                                        {submissionStatus}
                                                    </Text>
                                                </Stack>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                <IconButton
                                    disabled={groups.findIndex(({ id }) => id === currentGroupId) === 0}
                                    kind={'chevronLeft'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={() => dispatch({ type: 'SET_PREV_GROUP_ID' })}
                                />
                                <IconButton
                                    disabled={groups.findIndex(({ id }) => id === currentGroupId) === groups.length - 1}
                                    kind={'chevronRight'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={() => dispatch({ type: 'SET_NEXT_GROUP_ID' })}
                                />
                            </Stack>
                        </Stack>
                    </HeaderInnerBox>
                </HeaderBox>
                <CustomContainer device={device}>
                    <ContentStack direction={'row'} device={device}>
                        <LeftBox>{Submittable}</LeftBox>
                        <RightBox device={device}>{SubmissionData}</RightBox>
                    </ContentStack>
                </CustomContainer>
            </>
        );
    }

    return (
        <>
            <HeaderBox device={device}>
                <Stack sx={{ height: 160 }} justifyContent={'space-between'}>
                    <Stack spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <IconButton kind={'arrowLeft'} size={'medium'} variant={'square'} onClick={onClickBack} />
                            <Text noWrap variant={'sh1'} weight={'semibold'}>
                                {milestone?.title}
                            </Text>
                        </Stack>
                        <Text color={'light'} variant={'p2'}>
                            Due: {moment(milestone?.end_on).format('MMM DD [at] h:mma')}
                        </Text>
                    </Stack>
                    <Stack spacing={2}>
                        <Text color={'light'} variant={'p2'}>
                            Total Submissions: {Object.keys(submissionsByGroupId || {}).length}/{groups.length}
                        </Text>
                        <Text color={'light'} variant={'p2'}>
                            Viewing: {groups.findIndex(({ id }) => id === currentGroupId) + 1}/{groups.length}{' '}
                            Submissions
                        </Text>
                        <Select
                            value={currentGroup.id}
                            onChange={(e) => dispatch({ type: 'SET_CURRENT_GROUP_ID', payload: e.target.value })}
                        >
                            {groups.map((group) => {
                                const submissionStatus = !Object.keys(submissionsByGroupId || {}).includes(group.id)
                                    ? 'Missing'
                                    : 'Submitted';
                                return (
                                    <MenuItem key={group.id} value={group.id}>
                                        <Stack
                                            sx={{ width: '100%' }}
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                        >
                                            {group.name}
                                            <Text
                                                color={submissionStatus === 'Missing' ? 'light' : 'success'}
                                                variant={'p2'}
                                            >
                                                {submissionStatus}
                                            </Text>
                                        </Stack>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </Stack>
                </Stack>
            </HeaderBox>
            <CustomContainer device={device}>
                <ContentStack device={device}>
                    {Submittable}
                    <Box
                        sx={{
                            m: (theme) => theme.spacing(0, -5),
                            p: (theme) => theme.spacing(5, 5, 10),
                            background: (theme) => theme.palette.ensightful.shades[0],
                        }}
                    >
                        {SubmissionData}
                    </Box>
                </ContentStack>
            </CustomContainer>
        </>
    );
};

const HeaderBox = styled(Box)(({ device, theme }) => {
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        background: theme.palette.ensightful.shades[0],
        boxShadow: theme.palette.ensightful.shadows[1],
        zIndex: 1,
        ...(device !== 'mobile' && {
            minHeight: '88px',
            padding: theme.spacing(4, 8),
        }),
        ...(device === 'mobile' && {
            minHeight: '200px',
            padding: theme.spacing(5),
        }),
    };
});

const HeaderInnerBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: 'auto max(30%, 260px)',
        gap: theme.spacing(10),
        margin: 'auto',
        maxWidth: '1440px',
        height: '100%',
    };
});

const CustomContainer = styled(Container)(({ device, theme }) => {
    return {
        position: 'absolute',
        top: 0,
        left: 0,
        paddingTop: theme.spacing(22),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            paddingTop: theme.spacing(55),
        }),
        ...(device === 'desktop' && {
            left: '50%',
            transform: 'translateX(-50%)',
        }),
    };
});

const ContentStack = styled(Stack)(({ device, theme }) => {
    return {
        width: '100%',
        minHeight: 'calc(100vh - 152px)',
        ...(device === 'mobile' && {
            minHeight: 'unset',
        }),
        ...(device === 'tablet' && {
            margin: theme.spacing(0, -10, 0),
            width: `calc(100% + ${theme.spacing(10)})`,
        }),
        ...(device === 'desktop' && {
            margin: theme.spacing(0, -20, 0),
            width: `calc(100% + ${theme.spacing(20)})`,
        }),
    };
});

const LeftBox = styled(Box)(({ theme }) => {
    return {
        flex: 0.7,
        padding: theme.spacing(5),
    };
});

const PreviewBox = styled(Box)(({ device, theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[700]}`,
        height: '100%',
        padding: theme.spacing(5),
        ...(device === 'mobile' && {
            padding: theme.spacing(2),
        }),
    };
});

const RightBox = styled(Box)(({ device, theme }) => {
    return {
        flex: 0.3,
        width: '450px',
        padding: theme.spacing(6, 5, 10),
        boxShadow: theme.palette.ensightful.shadows[1],
        clipPath: 'inset(0 0 0 -15px)',
        ...(device === 'tablet' && {
            minWidth: '300px',
        }),
    };
});

const SubmissionListStack = styled(Stack)(({ theme }) => {
    return {
        marginTop: theme.spacing(3),
        marginLeft: theme.spacing(-5),
        width: 'calc(100% + 40px)',
        '& > div:nth-child(odd)': {
            background: theme.palette.ensightful.neutral[50],
        },
    };
});

const SubmissionStack = styled(Stack)(({ device, theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(5),
    };
});

const CustomAccordion = styled(Accordion)(() => {
    return {
        background: 'inherit',
    };
});

export default SubmissionPreview;
